/**
 * @name UserContext
 * @description Contexto de usuário adaptado para o projeto IS Cantina Client Web
 * @author Gabriel M. Guimarães | gabrielmg7
 * @package src/contexts/UserContext/UserContext.tsx
 */

import { Session } from '@supabase/supabase-js';
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseConfig';
import { useAlert } from '../../hooks/useAlert';
import { IPessoas } from '../../types/tables/IPessoas';
import { useLoadingContext } from '../LoadingContext/useLoadingContext';

export interface UserContextType {
     userSession: Session | null;
     setUserSession: React.Dispatch<React.SetStateAction<Session | null>>;
     userData: IPessoas | null;
     setUserData: React.Dispatch<React.SetStateAction<IPessoas | null>>;
     userType: number | null;
     setUserType: React.Dispatch<React.SetStateAction<number | null>>;
     signInWithPassword: (email: string, password: string) => Promise<string | null | void>;
     signUp: (pessoa: Partial<IPessoas>, email: string, password: string) => Promise<any | void>;
     signOut: () => Promise<void>;
     resetPassword: (email: string) => Promise<void>;
     updatePassword: (newPassword: string) => Promise<void>;
     updateEmail: (email: string) => Promise<void>;
     updateProfile: (profileData: { nome: string; email: string; documento_numero?: string; documento_tipo?: string; data_nascimento?: string }) => Promise<void>;
     resetInactivityTimer: () => void;
     forceRefreshAuth: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
     const navigate = useNavigate();
     const { showError, showSuccess } = useAlert();
     const { setLoadingState } = useLoadingContext();
     const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutos em milissegundos
     const inactivityTimerRef = useRef<NodeJS.Timeout>();
     const isProcessingAuthChangeRef = useRef(false);
     const isProcessingProfileOperationRef = useRef(false);
     const authLoopPreventionRef = useRef<{ lastRedirect: number; redirectCount: number }>({ lastRedirect: 0, redirectCount: 0 });
     const authTimeoutRef = useRef<NodeJS.Timeout | null>(null);
     const hasCheckedInitialSessionRef = useRef(false); // Ref para controlar verificação inicial de sessão

     const [userSession, setUserSession] = useState<Session | null>(null);
     const [userData, setUserData] = useState<IPessoas | null>(null);
     const [userType, setUserType] = useState<number | null>(null);

     // Função para prevenir loops de redirecionamento
     const preventAuthLoop = useCallback((): boolean => {
          const now = Date.now();
          const timeSinceLastRedirect = now - authLoopPreventionRef.current.lastRedirect;
          const currentPath = window.location.pathname;

          // IMPORTANTE: Não redireciona se está numa página que deveria processar integração
          if (currentPath.includes('/auth') && window.location.search.includes('ano_letivo')) {
               console.log('🚨 [UserContext] Bloqueando redirecionamento - página de integração ativa');
               return false;
          }

          // Se foi redirecionado há menos de 2 segundos
          if (timeSinceLastRedirect < 2000) {
               authLoopPreventionRef.current.redirectCount++;

               // Se houve mais de 3 redirecionamentos em 2 segundos, previne loop
               if (authLoopPreventionRef.current.redirectCount > 3) {
                    console.warn('🚨 [UserContext] Loop de redirecionamento detectado, pausando redirecionamentos');
                    return false; // Previne redirecionamento
               }
          } else {
               // Reset contador se passou tempo suficiente
               authLoopPreventionRef.current.redirectCount = 0;
          }

          // Registra o redirecionamento atual
          authLoopPreventionRef.current.lastRedirect = now;
          return true; // Permite redirecionamento
     }, []);

     const resetInactivityTimer = useCallback(() => {
          // Limpa o timer existente
          if (inactivityTimerRef.current) {
               clearTimeout(inactivityTimerRef.current);
          }

          // Se houver uma sessão ativa, configura o novo timer
          if (userSession) {
               inactivityTimerRef.current = setTimeout(async () => {
                    try {
                         // Limpa o localStorage e sessão
                         localStorage.removeItem('supabase.auth.token');
                         setUserSession(null);
                         setUserData(null);
                         setUserType(null);

                         // Tenta fazer logout no Supabase
                         await supabase.auth.signOut();

                         // Navega para a tela de login com proteção contra loop
                         if (preventAuthLoop()) {
                              navigate('/auth/login');
                         }

                         showError('Sessão expirada por inatividade');
                    } catch (error) {
                         if (preventAuthLoop()) {
                              navigate('/auth/login');
                         }
                    }
               }, INACTIVITY_TIMEOUT);
          }
     }, [userSession, navigate, showError]);

     // Monitor de atividade do usuário
     useEffect(() => {
          const events = ['mousedown', 'keydown', 'touchstart', 'mousemove', 'wheel', 'scroll'];

          const handleUserActivity = () => {
               resetInactivityTimer();
          };

          events.forEach((event) => {
               window.addEventListener(event, handleUserActivity);
          });

          // Configura o timer inicial
          resetInactivityTimer();

          return () => {
               if (inactivityTimerRef.current) {
                    clearTimeout(inactivityTimerRef.current);
               }
               events.forEach((event) => {
                    window.removeEventListener(event, handleUserActivity);
               });
          };
     }, [resetInactivityTimer]);

     const fetchUserData = async (userId: string, userType: 'auth_user_id' | 'id'): Promise<IPessoas | null> => {
          try {
               // Query direta e otimizada - sem verificações extras
               const { data, error } = await supabase.from('pessoas').select('*').eq(userType, userId).single();

               if (error) {
                    return null;
               }

               if (!data) {
                    return null;
               }

               return data as IPessoas;
          } catch (error) {
               return null;
          }
     };

     // Função para forçar refresh da autenticação (usado pela integração)
     const forceRefreshAuth = useCallback(async () => {
          console.log('🔄 [UserContext] Forçando refresh da autenticação');

          try {
               const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

               if (sessionError) {
                    console.error('❌ [UserContext] Erro ao buscar sessão:', sessionError);
                    return;
               }

               if (sessionData?.session) {
                    console.log('✅ [UserContext] Sessão encontrada, processando dados do usuário');
                    setUserSession(sessionData.session);
                    const userData = await fetchUserData(sessionData.session.user.id, 'auth_user_id');
                    if (userData) {
                         setUserData(userData);
                         setUserType(4); // Tipo padrão para integração
                         console.log('✅ [UserContext] Dados do usuário carregados via forceRefreshAuth', userData.id);
                    }
               } else {
                    console.log('⚠️ [UserContext] Nenhuma sessão encontrada no forceRefreshAuth');
               }
          } catch (error) {
               console.error('❌ [UserContext] Erro no forceRefreshAuth:', error);
          }
     }, [fetchUserData]);

     const handleAuthChange = useCallback(
          async (session: Session | null) => {
               // Evita execuções simultâneas
               if (isProcessingAuthChangeRef.current) {
                    console.log('🔄 Processamento de auth já em andamento, ignorando...', isProcessingAuthChangeRef.current ? 'true' : 'false');
                    return;
               }

               // Evita interferência durante operações de perfil
               if (isProcessingProfileOperationRef.current) {
                    console.log('🔄 Operação de perfil em andamento, ignorando...');
                    return;
               }

               // IMPORTANTE: Evita processamento durante integração externa (com menos logs)
               const isProcessingIntegration = localStorage.getItem('auth-integration-processing');
               if (isProcessingIntegration) {
                    // console.log('🔄 Integração externa em andamento, ignorando mudança de auth...');
                    return;
               }

               // PROTEÇÃO APRIMORADA: Verifica se a sessão realmente mudou
               const sessionChanged = (!userSession && session) || (userSession && !session) || userSession?.user?.id !== session?.user?.id;

               if (!sessionChanged && userData) {
                    console.log('⏸️ [UserContext] Sessão inalterada e dados já carregados, ignorando');
                    return;
               }

               // PROTEÇÃO EXTRA: Verifica timestamp da última mudança para evitar processamento muito rápido
               const lastChangeKey = 'last-auth-change';
               const lastChange = localStorage.getItem(lastChangeKey);
               const now = Date.now();

               if (lastChange) {
                    const timeDiff = now - parseInt(lastChange, 10);
                    if (timeDiff < 500) {
                         // Ignora mudanças muito rápidas (< 500ms)
                         console.log('⏸️ [UserContext] Mudança muito rápida detectada, ignorando:', timeDiff + 'ms');
                         return;
                    }
               }

               localStorage.setItem(lastChangeKey, now.toString());
               isProcessingAuthChangeRef.current = true;

               // Timeout de segurança para evitar loading infinito no UserContext
               authTimeoutRef.current = setTimeout(() => {
                    console.error('🚨 [UserContext] Timeout na autenticação (20s), forçando limpeza');
                    setLoadingState('auth-loading', false);
                    isProcessingAuthChangeRef.current = false;

                    // Se ainda não há sessão, força redirecionamento com proteção
                    if (!session && preventAuthLoop()) {
                         navigate('/auth/login');
                    }
               }, 20000);

               try {
                    // Só ativa loading se ainda não estiver ativo (evita conflito com integração)
                    const currentAuthLoading = localStorage.getItem('auth-loading-active');
                    if (!currentAuthLoading) {
                         setLoadingState('auth-loading', true);
                    }

                    setUserSession(session);

                    if (session && session.user && session.user.id) {
                         const userId = session.user.id;

                         const data = await fetchUserData(userId, 'auth_user_id');

                         if (data) {
                              setUserData(data);
                              setUserType(data.fk_id_tipo_pessoa); // Se está em uma rota de auth e já está logado, redireciona para home
                              // EXCETO se está processando integração ou na página de integração
                              const currentPath = window.location.pathname;
                              const isIntegrationProcessing = localStorage.getItem('auth-integration-processing');
                              const isIntegrationPage = currentPath.includes('/auth/integration') || currentPath === '/login';

                              console.log('🔍 [UserContext] Verificando redirecionamento:', {
                                   currentPath,
                                   isIntegrationProcessing: !!isIntegrationProcessing,
                                   isIntegrationPage,
                                   shouldRedirectFromAuth: currentPath.startsWith('/auth') && !isIntegrationProcessing && !isIntegrationPage,
                                   shouldRedirectFromLogin: currentPath === '/login' && !isIntegrationProcessing,
                              });

                              if (currentPath.startsWith('/auth') && !isIntegrationProcessing && !isIntegrationPage) {
                                   console.log('✅ [UserContext] Usuário autenticado, redirecionando de /auth para /');
                                   navigate('/');
                              } else if (currentPath === '/login' && !isIntegrationProcessing) {
                                   console.log('✅ [UserContext] Usuário autenticado na rota /login, redirecionando para /');
                                   navigate('/');
                              } else if (currentPath === '/login' && isIntegrationProcessing) {
                                   console.log('🔄 [UserContext] Usuário autenticado em /login durante integração - aguardando conclusão do processo');
                              }
                         } else {
                              // Se não encontrou o usuário, fazer logout
                              console.log('❌ [UserContext] Usuário não encontrado no banco, fazendo logout');
                              await supabase.auth.signOut();
                              setUserData(null);
                              setUserType(null);
                              setUserSession(null);
                              if (preventAuthLoop()) {
                                   console.log('🔄 [UserContext] Redirecionando para /auth/login devido a usuário não encontrado');
                                   navigate('/auth/login');
                              } else {
                                   console.log('🚨 [UserContext] Redirecionamento bloqueado por loop prevention');
                              }
                         }
                    } else {
                         setUserData(null);
                         setUserType(null);
                    }
               } catch (error) {
                    // Em caso de erro, garante que os dados sejam limpos
                    setUserData(null);
                    setUserType(null);
               } finally {
                    // Limpa timeout de segurança
                    if (authTimeoutRef.current) {
                         clearTimeout(authTimeoutRef.current);
                         authTimeoutRef.current = null;
                    }

                    // Garante que o loading seja sempre desativado
                    setLoadingState('auth-loading', false);
                    isProcessingAuthChangeRef.current = false;
               }
          },
          [navigate, setLoadingState], // Removidas dependências que causavam re-execuções desnecessárias
     );

     // Setup do listener de autenticação
     useEffect(() => {
          const checkInitialSession = async () => {
               // Evita múltiplas verificações de sessão inicial
               if (hasCheckedInitialSessionRef.current) {
                    console.log('⏸️ [UserContext] Sessão inicial já verificada, ignorando');
                    return;
               }

               hasCheckedInitialSessionRef.current = true;
               console.log('🔍 [UserContext] Verificando sessão inicial...');

               // IMPORTANTE: Verifica se há parâmetros de integração na URL atual
               const urlParams = new URLSearchParams(window.location.search);
               const hasIntegrationParams = urlParams.has('ano_letivo') && urlParams.has('matricula');

               if (hasIntegrationParams) {
                    console.log('🎯 [UserContext] Parâmetros de integração detectados, delegando para middleware');
                    return; // Deixa o middleware cuidar da integração
               }

               const { data, error } = await supabase.auth.getSession();
               if (error) {
                    console.log('❌ [UserContext] Erro na verificação inicial de sessão:', error);
                    if (preventAuthLoop()) {
                         console.log('🔄 [UserContext] Redirecionando para /auth/login devido a erro na sessão');
                         navigate('/auth/login');
                    }
               } else {
                    console.log('✅ [UserContext] Sessão inicial verificada:', { hasSession: !!data.session });
                    const session = data.session;
                    await handleAuthChange(session);
               }
          };

          checkInitialSession();
          const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
               console.log('🔄 [UserContext] onAuthStateChange disparado:', { event, sessionId: session?.user?.id });

               // PROTEÇÃO: Ignora eventos redundantes quando o usuário já está autenticado
               if (userSession && session && userSession.user?.id === session.user?.id) {
                    console.log('⏸️ [UserContext] Mesma sessão já ativa, ignorando mudança');
                    return;
               }

               // PROTEÇÃO: Se está minimizando/maximizando (evento SIGNED_IN para usuário já logado)
               if (event === 'SIGNED_IN' && userSession && session && userSession.user?.id === session.user?.id) {
                    console.log('⏸️ [UserContext] Evento SIGNED_IN ignorado - usuário já autenticado');
                    return;
               }

               // PROTEÇÃO ADICIONAL: Ignora eventos TOKEN_REFRESHED quando não há mudança real
               if (event === 'TOKEN_REFRESHED' && userSession && session && userSession.user?.id === session.user?.id) {
                    console.log('⏸️ [UserContext] TOKEN_REFRESHED ignorado - dados inalterados');
                    return;
               }

               // CORREÇÃO ESPECÍFICA PARA CHROME/BRAVE/EDGE: Adiciona delay para evitar loop
               const userAgent = navigator.userAgent.toLowerCase();
               const isChromiumBased = userAgent.includes('chrome') || userAgent.includes('brave') || userAgent.includes('edg/');

               if (isChromiumBased && event !== 'SIGNED_OUT') {
                    console.log('🌐 [UserContext] Navegador Chromium detectado, aplicando delay de 100ms');
                    setTimeout(async () => {
                         await handleAuthChange(session);
                    }, 100); // Reduzido de 150ms para 100ms
               } else {
                    console.log('🌐 [UserContext] Processando mudança de auth imediatamente');
                    await handleAuthChange(session);
               }
          });

          return () => {
               authListener?.subscription.unsubscribe();

               // Limpa timeout de autenticação se existir
               if (authTimeoutRef.current) {
                    clearTimeout(authTimeoutRef.current);
                    authTimeoutRef.current = null;
               }
          };
     }, [handleAuthChange, navigate]);

     const signUp = async (pessoa: Partial<IPessoas>, email: string, password: string) => {
          try {
               setLoadingState('auth-loading', true);

               // 1. Criar usuário no Supabase Auth
               const { data: authResponse, error: authError } = await supabase.auth.signUp({
                    email,
                    password,
               });

               if (authError) {
                    throw authError;
               }

               if (authResponse?.user) {
                    // 2. Criar registro na tabela pessoas
                    const pessoaData: any = {
                         auth_user_id: authResponse.user.id,
                         nome: pessoa.nome || 'Usuário',
                         email: email,
                         documento_tipo: pessoa.documento_tipo || 'CPF',
                         documento_numero: pessoa.documento_numero || '00000000000',
                         data_nascimento: pessoa.data_nascimento || null,
                         fk_id_tipo_pessoa: pessoa.fk_id_tipo_pessoa || 4, // Default: Responsável
                         fk_id_organizacao: pessoa.fk_id_organizacao || null,
                         matricula: null, // Campo obrigatório adicionado
                         ano_letivo: null, // Campo obrigatório adicionado
                         ativo: true,
                         created_by: 1, // Sistema
                         created_at: new Date().toISOString(),
                         updated_at: null,
                         updated_by: null,
                         deleted_at: null,
                         deleted_by: null,
                         cod_integracao: null,
                    };

                    const { data: pessoaResponse, error: pessoaError } = await supabase.from('pessoas').insert(pessoaData).select().single();

                    if (pessoaError) {
                         throw pessoaError;
                    }

                    return [pessoaResponse, authResponse];
               }
          } catch (error: any) {
               showError(`Erro ao criar usuário: ${error.message}`);
          } finally {
               setLoadingState('auth-loading', false);
          }
     };

     const signInWithPassword = async (email: string, password: string) => {
          try {
               const {
                    data: { user },
                    error,
               } = await supabase.auth.signInWithPassword({ email, password });

               if (error) {
                    throw error;
               } else if (user) {
                    return user.id;
               } else {
                    throw new Error('Nenhum usuário retornado do Supabase');
               }
          } catch (error: any) {
               // Tratamento específico para diferentes tipos de erro
               let errorMessage = 'Erro ao fazer login';

               if (error.message?.includes('Invalid login credentials')) {
                    errorMessage = 'Email ou senha incorretos';
               } else if (error.message?.includes('Email not confirmed')) {
                    errorMessage = 'Email não confirmado. Verifique sua caixa de entrada';
               } else if (error.message?.includes('Too many requests')) {
                    errorMessage = 'Muitas tentativas de login. Tente novamente em alguns minutos';
               } else if (error.message?.includes('User not found')) {
                    errorMessage = 'Usuário não encontrado';
               } else if (error.message) {
                    errorMessage = error.message;
               }

               showError(errorMessage);
               // Em caso de erro, desativa o loading que pode ter sido ativado pelo handleAuthChange
               setLoadingState('auth-loading', false);
               return null;
          }
     };

     const signOut = async () => {
          try {
               setLoadingState('auth-loading', true);

               // Limpa os dados do usuário primeiro
               setUserData(null);
               setUserType(null);
               setUserSession(null);

               // Limpa qualquer token no localStorage
               localStorage.removeItem('supabase.auth.token');
               localStorage.clear();

               // Tenta fazer o signOut no Supabase
               await supabase.auth.signOut().catch(() => {});

               // Força atualização do cliente Supabase
               supabase.auth.startAutoRefresh();

               // Navega para a tela de login
               navigate('/auth/login');
          } catch (error: any) {
               // Mesmo com erro, vamos tentar limpar tudo
               setUserData(null);
               setUserType(null);
               setUserSession(null);
               localStorage.clear();
               navigate('/auth/login');
          } finally {
               setLoadingState('auth-loading', false);
          }
     };

     const resetPassword = async (email: string) => {
          try {
               setLoadingState('auth-loading', true);
               const { error } = await supabase.auth.resetPasswordForEmail(email);
               if (error) {
                    throw error;
               }
               showSuccess('Email de recuperação enviado com sucesso');
          } catch (error: any) {
               showError(`Erro ao resetar senha: ${error.message}`);
          } finally {
               setLoadingState('auth-loading', false);
          }
     };

     const updatePassword = async (newPassword: string) => {
          isProcessingProfileOperationRef.current = true;

          try {
               setLoadingState('profile-loading', true);

               const result = await supabase.auth.updateUser({
                    password: newPassword,
               });

               if (result.error) {
                    throw result.error;
               }

               showSuccess('Senha atualizada com sucesso');
          } catch (error: any) {
               let errorMessage = 'Erro ao atualizar senha';

               if (error.code === 'same_password') {
                    errorMessage = 'A nova senha deve ser diferente da senha atual';
               } else if (error.message?.includes('same password')) {
                    errorMessage = 'A nova senha deve ser diferente da senha atual';
               } else if (error.message?.includes('weak_password')) {
                    errorMessage = 'A senha deve ter pelo menos 6 caracteres';
               } else if (error.message?.includes('too_many_requests')) {
                    errorMessage = 'Muitas tentativas. Tente novamente em alguns minutos';
               } else if (error.message) {
                    errorMessage = error.message;
               }

               showError(errorMessage);
          } finally {
               setLoadingState('profile-loading', false);
               isProcessingProfileOperationRef.current = false;
          }
     };

     const updateEmail = async (email: string) => {
          isProcessingProfileOperationRef.current = true;

          try {
               setLoadingState('profile-loading', true);

               const { error } = await supabase.auth.updateUser({
                    email,
               });

               if (error) {
                    throw error;
               }

               showSuccess('Email atualizado com sucesso');
          } catch (error: any) {
               let errorMessage = 'Erro ao atualizar e-mail';

               if (error.code === 'email_address_invalid') {
                    errorMessage = 'Endereço de e-mail inválido';
               } else if (error.message?.includes('invalid email')) {
                    errorMessage = 'Endereço de e-mail inválido';
               } else if (error.message?.includes('already exists')) {
                    errorMessage = 'Este e-mail já está em uso';
               } else if (error.message?.includes('too_many_requests')) {
                    errorMessage = 'Muitas tentativas. Tente novamente em alguns minutos';
               } else if (error.message) {
                    errorMessage = error.message;
               }

               showError(errorMessage);
          } finally {
               setLoadingState('profile-loading', false);
               isProcessingProfileOperationRef.current = false;
          }
     };

     const updateProfile = async (profileData: { nome: string; email: string; documento_numero?: string; documento_tipo?: string; data_nascimento?: string }) => {
          isProcessingProfileOperationRef.current = true;

          try {
               setLoadingState('profile-loading', true);

               // Preparar dados para atualização
               const updateData: any = {
                    nome: profileData.nome,
                    email: profileData.email,
                    updated_at: new Date().toISOString(),
                    updated_by: userData?.id!,
               };

               // Adicionar campos opcionais se fornecidos
               if (profileData.documento_numero) {
                    updateData.documento_numero = profileData.documento_numero;
               }
               if (profileData.documento_tipo) {
                    updateData.documento_tipo = profileData.documento_tipo;
               }
               if (profileData.data_nascimento) {
                    updateData.data_nascimento = profileData.data_nascimento;
               }

               // 1. Atualizar dados na tabela pessoas
               const { error: updateError } = await supabase.from('pessoas').update(updateData).eq('id', userData?.id!);

               if (updateError) {
                    throw updateError;
               }

               // 2. Se o email mudou, atualizar também no Supabase Auth
               if (profileData.email !== userData?.email) {
                    const { error: authError } = await supabase.auth.updateUser({
                         email: profileData.email,
                    });

                    if (authError) {
                         throw authError;
                    }
               }

               // 3. Atualizar o estado local
               setUserData((prev) =>
                    prev
                         ? {
                                ...prev,
                                ...updateData,
                           }
                         : null,
               );

               showSuccess('Perfil atualizado com sucesso!');
          } catch (error: any) {
               let errorMessage = 'Erro ao atualizar perfil';

               if (error.code === 'email_address_invalid') {
                    errorMessage = 'Endereço de e-mail inválido';
               } else if (error.message?.includes('invalid email')) {
                    errorMessage = 'Endereço de e-mail inválido';
               } else if (error.message?.includes('already exists') || error.message?.includes('duplicate')) {
                    errorMessage = 'Este e-mail já está em uso';
               } else if (error.message?.includes('too_many_requests')) {
                    errorMessage = 'Muitas tentativas. Tente novamente em alguns minutos';
               } else if (error.message) {
                    errorMessage = error.message;
               }

               showError(errorMessage);
               throw error; // Re-throw para que o componente possa tratar
          } finally {
               setLoadingState('profile-loading', false);
               isProcessingProfileOperationRef.current = false;
          }
     };

     return (
          <UserContext.Provider
               value={{
                    userSession,
                    setUserSession,
                    userData,
                    setUserData,
                    userType,
                    setUserType,
                    signInWithPassword,
                    signUp,
                    signOut,
                    resetPassword,
                    updatePassword,
                    updateEmail,
                    updateProfile,
                    resetInactivityTimer,
                    forceRefreshAuth,
               }}
          >
               {children}
          </UserContext.Provider>
     );
};

export default UserContext;
